import type { RouteLocationNormalized } from 'vue-router';
import { isAxiosError } from 'axios';
import { useUserStore } from '@/stores/userStore';
import { useMainStore } from '@/stores/mainStore';
import { useAuth } from '@/composables/useAuth';
import { USER_PERMISSION_ROLE_ENUM } from '@/types/permissions';
import { updateLDContext, getFlag } from '@/plugins/launchdarkly';
import { isAuthenticated } from '@/stores/authStore';
import { useAppErrorStore } from '@/stores/appErrorStore';
import { datadogLogs } from '@datadog/browser-logs';
import { bootstrapSession } from './sessionBootstrap';

export { bootstrapSession, resetSessionBootstrap } from './sessionBootstrap';

export function isSuperAdminRouteGuard() {
  const { user } = useUserStore();
  if (!user?.isSuperAdmin) {
    return { name: 'NotFound' };
  }
}

/**
 * Orchestrates the full authenticated-navigation flow for router.beforeEach: confirms
 * the user is authenticated, hydrates the app switcher, bootstraps the session (fetching
 * /me and identifying the LaunchDarkly client), and checks any route-required LD flags.
 *
 * A 401/403 from bootstrapSession() means the token existed locally (isAuthenticated()
 * only checks presence, not server-side validity) but has since expired or been revoked —
 * treated the same as the unauthenticated case. Any other bootstrap failure is critical
 * and app-breaking, so it's surfaced through the shared appErrorStore (read by
 * ErrorBoundary) instead of letting the app continue rendering in a broken,
 * half-bootstrapped state.
 */
export async function handleAuthenticatedNavigation(to: RouteLocationNormalized) {
  if (!isAuthenticated()) {
    useAuth().logOut();
    return { name: 'UnauthenticatedView' };
  }

  await useUserStore().setAppSwitcher();

  try {
    await bootstrapSession();
    useAppErrorStore().clearCriticalError();
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status === 401 || status === 403) {
      useAuth().logOut();
      return { name: 'UnauthenticatedView' };
    }
    useAppErrorStore().setCriticalError(error as Error);
    return false;
  }

  if (to.meta.requiredLDFlags?.length) {
    // getFlag() reads synchronously from whatever context the client was last identified
    // with. That's fine for the common case (bootstrapSession() already identified it),
    // but a team switch re-identifies asynchronously via App.vue's teamTwrn watcher, so a
    // flag-gated navigation landing in that same window could otherwise read a stale
    // value. Re-syncing here (only when flags are actually required) either awaits that
    // in-flight identify() or is a cheap no-op if the context hasn't changed.
    try {
      await updateLDContext();
    } catch (error) {
      // Fail closed: a rejected re-sync means getFlag() may still reflect a stale
      // context from before the team switch, so deny rather than risk granting access.
      datadogLogs.logger.error('[LaunchDarkly] Failed to re-sync context before required-flag check', {
        error: error instanceof Error
          ? {
              message: error.message,
              stack: error.stack,
            }
          : String(error),
      });
      return { name: 'NoAccess' };
    }
    if (to.meta.requiredLDFlags.some((flag) => !getFlag(flag, false))) {
      return { name: 'NoAccess' };
    }
  }

  return true;
}

export async function canAccessAdminRoutes() {
  const userStore = useUserStore();
  const mainStore = useMainStore();
  const userRole = userStore.permissions?.find((permission) => permission.twrn === mainStore.teamTwrn);
  if (userRole?.role !== USER_PERMISSION_ROLE_ENUM.ADMIN) {
    return { name: 'NotFound' };
  }
}

export async function canAccessAdminRoutesWithSuperAdmin() {
  if (!isSuperAdminRouteGuard()) return; // If the function returns void, the guard has passed.
  return canAccessAdminRoutes();
}
